import { ACCOUNT_SEGMENT_PARAM_KEYS, minMaxParamFilters } from 'dashboard/utils'

// The filter half of a text chart's data fields: the {{filter::…}} tokens that
// echo what the dashboard is filtered by, as opposed to the {{Column:agg}}
// tokens that quote the data itself (helpers/data-field-tokens).
//
// The key is an internal accessor/param key, so report authors have no way to
// know that the AP invoice date range lives under `apInvoiceDate`.

// Filter types whose params interpolate into readable text. Deliberately an
// allowlist rather than an exclusion list: `reference` (employee filters) and
// the account/vendor columns below store arrays of objects or of database IDs,
// which would render as "[object Object]" or "12, 47" — accurate to the params
// and useless in a report. Leave them out until they have real formatting.
//
// `group_multiselect` (the account rollups) is out for the same reason: its
// params hold AccountGroup *ids*, not labels — names are user-editable, so
// filtering by name would break saved Views (AccountGroupFilterPopover) — and a
// token for one would print a row of UUIDs.
const SUPPORTED_FILTER_TYPES = [
  ...minMaxParamFilters,
  'basic',
  'greater_than',
  'select',
  'multiselect',
  'mcc_select'
]

// Vendor filters live in params.vendors / params.excludedVendors as objects,
// not under the column's accessor_key.
const VENDOR_COLUMN_NAME = 'vendor_name'

export const isEmbeddableColumn = (column) => {
  if (!column?.accessor_key) return false
  if (ACCOUNT_SEGMENT_PARAM_KEYS[column.database_column_name]) return false
  if (column.database_column_name === VENDOR_COLUMN_NAME) return false
  return SUPPORTED_FILTER_TYPES.includes(column.filter_type)
}

export const filterToken = (key) => `{{filter::${key}}}`

// Range endpoints read differently by type: a date range has a start and an
// end, an amount range a minimum and a maximum.
const boundLabels = (column) =>
  column.filter_type === 'date'
    ? { min: 'Start', max: 'End' }
    : { min: 'Minimum', max: 'Maximum' }

/**
 * Every filter value a column can print, labelled for a menu already scoped to
 * that column. Min/max columns carry the whole range plus each endpoint; date
 * columns additionally carry the relative option the author picked ("Previous
 * Fiscal Year"), which reads better in a sentence than the dates it resolves to.
 *
 * @returns {Array} [{ label, key, token }] — `key` is what resolveFilterValue takes
 */
export const filterTokenOptions = (column) => {
  if (!isEmbeddableColumn(column)) return []

  const key = column.accessor_key
  if (!minMaxParamFilters.includes(column.filter_type)) {
    return [{ label: 'Value', key, token: filterToken(key) }]
  }

  const bounds = boundLabels(column)
  const entries = [
    { label: 'Range', key, token: filterToken(key) },
    { label: bounds.min, key: `${key}.min`, token: filterToken(`${key}.min`) },
    { label: bounds.max, key: `${key}.max`, token: filterToken(`${key}.max`) }
  ]

  if (column.filter_type === 'date') {
    entries.push({ label: 'Selection', key: `${key}.option`, token: filterToken(`${key}.option`) })
  }

  return entries
}
